class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  // Hover-to-open/close for the header menu, for both the dropdown-style
  // submenu (.header__submenu) and the mega-menu-style panel
  // (.mega-menu__content) - this theme's menu_type_desktop setting can
  // route through either structure. Both already show/hide correctly via
  // this theme's own native `details[open]` / `.mega-menu[open]` CSS
  // rules, so this class's job is just to manage *when* the `open`
  // attribute gets set and removed in response to hover.
  //
  // The close decision is based on leaving the whole header bar
  // (.header-wrapper), not just this menu's own small trigger element.
  // Both submenu styles can render with real vertical distance between the
  // trigger and the panel (the mega-menu panel positions relative to the
  // entire header bar, not the trigger, and can sit a visible gap below
  // it) - tracking at the trigger level meant moving the mouse down
  // through that gap left the trigger's own hover state and triggered an
  // early close, even though the mouse never actually left the header bar
  // as a whole. Tracking at the header level instead means that movement
  // never risks closing the menu.
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');

    // Open/close on hover for desktop pointer devices only (mouse/trackpad).
    // Touch devices keep the existing tap-to-toggle behavior untouched.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.addEventListener('mouseenter', this.onMouseEnter.bind(this));

      if (this.header) {
        this.headerMouseLeaveHandler = this.onHeaderMouseLeave.bind(this);
        this.header.addEventListener('mouseleave', this.headerMouseLeaveHandler);
      } else {
        // Fallback if .header-wrapper isn't found for some reason - close
        // based on this element's own leave, same as before.
        this.addEventListener('mouseleave', this.onHeaderMouseLeave.bind(this));
      }
    }
  }

  onMouseEnter() {
    clearTimeout(this.hoverCloseTimeout);

    // Close any other open header menu first, so hovering a new top-level
    // item (e.g. moving from "Men" straight to "Women") swaps which menu
    // is open instead of leaving both open at once - necessary now that
    // closing isn't tied to leaving each menu's own small trigger area.
    if (this.header) {
      this.header.querySelectorAll('header-menu').forEach((el) => {
        if (el !== this && el.mainDetailsToggle && el.mainDetailsToggle.hasAttribute('open')) {
          el.close();
        }
      });
    }

    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
  }

  onHeaderMouseLeave() {
    if (!this.mainDetailsToggle.hasAttribute('open')) return;
    clearTimeout(this.hoverCloseTimeout);
    // Standard hover-intent delay (300ms) before closing - just enough to
    // not feel twitchy on small, accidental mouse movements, without
    // feeling like the menu is stuck open. The header-wide tracking above
    // already prevents the "closes before I can reach a link" bug this was
    // originally lengthened to work around, so a short delay here is safe.
    this.hoverCloseTimeout = setTimeout(() => {
      this.close();
    }, 300);
  }

  close() {
    super.close();
    clearTimeout(this.hoverCloseTimeout);
    this.hoverCloseTimeout = null;
  }

  disconnectedCallback() {
    if (this.header && this.headerMouseLeaveHandler) {
      this.header.removeEventListener('mouseleave', this.headerMouseLeaveHandler);
    }
  }

  onToggle() {
    super.onToggle();

    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
